import React from "react";
import { NavLink } from "react-router-dom";
import {
  CALENDLY_URL,
  CONTACT_EMAIL,
  FZUL_URL,
  HABA_URL,
  TRIPROCK_URL,
} from "@/constants";
import { langPath, useLang, useTranslation } from "@/i18n";

export const Footer: React.FC = () => {
  const t = useTranslation();
  const lang = useLang();

  const projectLinks = [
    { name: t.footer.triprockLabel, url: TRIPROCK_URL },
    { name: "fzul.ai", url: FZUL_URL },
    { name: "haba.ai", url: HABA_URL },
  ];

  const scrollTo = (elementId: string) => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-[#1d2128] border-t border-white/10 pt-[48px] md:pt-[64px] pb-[24px]">
      <div className="max-w-screen-xl m-auto px-[38px] 2xl:px-0">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center">
              <img
                src="/images/rckntvlogo.png"
                alt="Rocknitive Logo"
                className="w-[36px] mr-3"
              />
              <span className="text-[18px] font-semibold text-[#01C6FB]">
                Rocknitive
              </span>
            </div>
            <p className="mt-4 text-[14px] leading-[23px] text-base-400">
              {t.footer.brand}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <p className="text-[13px] font-semibold tracking-[0.15em] uppercase text-base-400">
              {t.footer.navLabel}
            </p>
            <ul className="mt-4 space-y-2">
              {t.footer.nav.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="cursor-pointer text-[15px] text-base-300 hover:text-[#01C6FB] transition-colors"
                    onClick={(event) => {
                      event.preventDefault();
                      scrollTo(item.id);
                    }}
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Brands & projects */}
          <div>
            <p className="text-[13px] font-semibold tracking-[0.15em] uppercase text-base-400">
              {t.footer.projectsLabel}
            </p>
            <ul className="mt-4 space-y-3">
              {projectLinks.map((project) => (
                <li key={project.url}>
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[15px] text-base-300 hover:text-[#01C6FB] transition-colors"
                  >
                    {project.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="text-[13px] font-semibold tracking-[0.15em] uppercase text-base-400">
              {t.footer.contactLabel}
            </p>
            <ul className="mt-4 space-y-3">
              <li>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-[15px] text-base-300 hover:text-[#01C6FB] transition-colors"
                >
                  {CONTACT_EMAIL}
                </a>
              </li>
              <li>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[15px] font-semibold text-[#01C6FB] hover:underline"
                >
                  {t.footer.bookCall}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-[48px] border-t border-white/10 pt-[20px] text-center text-[13px] text-base-400">
          © {new Date().getFullYear()} Rocknitive GmbH ·{" "}
          <NavLink
            to={langPath("/imprint", lang)}
            className="hover:text-[#01C6FB] transition-colors"
          >
            {t.footer.imprint}
          </NavLink>{" "}
          ·{" "}
          <NavLink
            to={langPath("/privacy-policy", lang)}
            className="hover:text-[#01C6FB] transition-colors"
          >
            {t.footer.privacy}
          </NavLink>
        </div>
      </div>
    </div>
  );
};
